import { useState, useMemo, useEffect } from "react";
import { useCollectionsQuery } from "@/lib/queries";
import { MessageCircle, ShoppingBag, Check, Eye, Search, X, ChevronLeft } from "lucide-react";
import { motion } from "framer-motion";
import { useSettings, buildWhatsAppLink } from "@/context/SettingsContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useCart } from "@/context/CartContext";
import { isSaleActive, effectivePriceGbp, discountPercent } from "@/lib/sale";
import ProductDetailsModal from "@/components/ProductDetailsModal";
import CategoryCard from "@/components/CategoryCard";
import { CATEGORIES, resolveCategoryCardImage } from "@/lib/categories";
import { trackAddToCart } from "@/lib/analytics";

const WIG_COLLECTIONS = ["All Wigs", "Signature Collection", "Heritage Collection"];

// Normalises a value for case-insensitive substring matching.
const norm = (v) => String(v || "").toLowerCase().trim();

// Resolves the display value for a product's origin (uses custom override when set).
const originOf = (c) => (c.hair_origin === "Other" ? c.hair_origin_custom : c.hair_origin) || "";

export default function Collections() {
  const { settings } = useSettings();
  const { data: items = [] } = useCollectionsQuery();
  const { formatPrice, currency, getGbpNumber } = useCurrency();
  const { addItem } = useCart();
  const [activeCategory, setActiveCategory] = useState(null); // null → show category landing cards
  const [activeWigCollection, setActiveWigCollection] = useState("All Wigs");
  const [searchQuery, setSearchQuery] = useState("");
  const [textureFilter, setTextureFilter] = useState("");
  const [originFilter, setOriginFilter] = useState("");
  const [openProduct, setOpenProduct] = useState(null);
  const [justAdded, setJustAdded] = useState(null);

  // Homepage "Shop by Category" cards dispatch this to jump us into a category.
  useEffect(() => {
    const handler = (e) => {
      if (typeof e.detail === "string") setActiveCategory(e.detail);
    };
    window.addEventListener("demj:select-category", handler);
    return () => window.removeEventListener("demj:select-category", handler);
  }, []);

  const handleAdd = (c) => {
    const priceString = effectivePriceGbp(c);
    const amount = getGbpNumber(priceString);
    addItem({
      id: c.id,
      name: c.name,
      category: c.category || "Luxury Wigs",
      price_gbp_string: priceString,
      unit_amount_gbp: amount || 0,
      image: c.image,
      quantity: 1,
    });
    trackAddToCart(c, 1);
    setJustAdded(c.id);
    setTimeout(() => setJustAdded((curr) => (curr === c.id ? null : curr)), 1400);
  };

  // Unique dropdown options — derived from actual product data so nothing goes stale.
  const textureOptions = useMemo(() => {
    const set = new Set();
    items.forEach((c) => { if (c.texture) set.add(c.texture); });
    return Array.from(set).sort();
  }, [items]);

  const originOptions = useMemo(() => {
    const set = new Set();
    items.forEach((c) => {
      const o = originOf(c);
      if (o) set.add(o);
    });
    return Array.from(set).sort();
  }, [items]);

  const filtered = useMemo(() => {
    let list = items;
    if (activeCategory && activeCategory !== "All") {
      list = list.filter((c) => (c.category || "Luxury Wigs") === activeCategory);
    }
    if (activeCategory === "Luxury Wigs" && activeWigCollection !== "All Wigs") {
      list = list.filter((c) => (c.wig_collection || "") === activeWigCollection);
    }
    if (textureFilter) {
      list = list.filter((c) => (c.texture || "") === textureFilter);
    }
    if (originFilter) {
      list = list.filter((c) => originOf(c) === originFilter);
    }
    const q = norm(searchQuery);
    if (q) {
      list = list.filter((c) => {
        const haystack = [
          c.name,
          c.description,
          c.category,
          c.wig_collection,
          c.texture,
          c.hair_type,
          c.hair_origin,
          c.hair_origin_custom,
          c.lace_type,
          c.lace_size,
          c.length,
          c.density,
        ].map(norm).join(" | ");
        return haystack.includes(q);
      });
    }
    return list;
  }, [items, activeCategory, activeWigCollection, searchQuery, textureFilter, originFilter]);

  const anyFilter = searchQuery || textureFilter || originFilter || (activeCategory && activeCategory !== "All") || activeWigCollection !== "All Wigs";
  const clearAllFilters = () => {
    setSearchQuery("");
    setTextureFilter("");
    setOriginFilter("");
    setActiveCategory("All");
    setActiveWigCollection("All Wigs");
  };

  return (
    <section id="collections" className="relative py-24 md:py-32 bg-[#0F0F0F]" data-testid="collections-section">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="text-center mb-10">
          <div className="text-[0.7rem] tracking-[0.32em] uppercase text-[#D4AF37] font-semibold mb-4" data-testid="collections-eyebrow">
            {settings?.collections_eyebrow}
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white max-w-2xl mx-auto" data-testid="collections-headline">
            {settings?.collections_headline}
          </h2>
        </div>

        {/* Category landing — shown until the customer picks a category. */}
        {activeCategory === null && (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 mb-16"
            data-testid="collections-category-landing"
          >
            {CATEGORIES.map((cat, i) => {
              const image = resolveCategoryCardImage(
                cat.value,
                settings?.category_card_images,
                items,
              );
              return (
                <motion.div
                  key={cat.value}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.1 }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                >
                  <CategoryCard
                    label={cat.label}
                    image={image}
                    onClick={() => setActiveCategory(cat.value)}
                    testId={`collections-landing-card-${cat.slug}`}
                    size={cat.value === "All" ? "lg" : "md"}
                    aspect={cat.value === "All" ? "16/9" : "4/5"}
                  />
                </motion.div>
              );
            })}
          </div>
        )}

        {activeCategory !== null && (
          <>
            <div className="mb-6 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setActiveCategory(null)}
                className="inline-flex items-center gap-1.5 text-[0.7rem] uppercase tracking-[0.22em] text-white/55 hover:text-[#D4AF37] transition-colors"
                data-testid="collections-back-to-categories"
              >
                <ChevronLeft className="w-4 h-4" /> Back to categories
              </button>
              <div className="text-[0.65rem] uppercase tracking-[0.28em] text-[#D4AF37]" data-testid="collections-active-category-chip">
                {CATEGORIES.find((c) => c.value === activeCategory)?.label || activeCategory}
              </div>
            </div>

        {/* Search + Filter bar */}
        <div className="mb-8 flex flex-col md:flex-row gap-3 items-stretch md:items-center" data-testid="search-filter-bar">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" aria-hidden />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, texture, origin, closure size…"
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none pl-10 pr-9 py-2.5 text-white text-sm placeholder:text-white/35"
              data-testid="collections-search-input"
              aria-label="Search products"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                aria-label="Clear search"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/50 hover:text-[#D4AF37] p-1"
                data-testid="collections-search-clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <select
              value={textureFilter}
              onChange={(e) => setTextureFilter(e.target.value)}
              className="bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2.5 text-white text-sm uppercase tracking-[0.12em]"
              data-testid="collections-filter-texture"
              aria-label="Filter by texture"
            >
              <option value="">All Textures</option>
              {textureOptions.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <select
              value={originFilter}
              onChange={(e) => setOriginFilter(e.target.value)}
              className="bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2.5 text-white text-sm uppercase tracking-[0.12em]"
              data-testid="collections-filter-origin"
              aria-label="Filter by origin"
            >
              <option value="">All Origins</option>
              {originOptions.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
            {anyFilter && (
              <button
                type="button"
                onClick={clearAllFilters}
                className="text-[0.65rem] uppercase tracking-[0.22em] text-white/50 hover:text-[#D4AF37] px-3 border border-[#2A2A2A] hover:border-[#D4AF37]/60 transition"
                data-testid="collections-clear-filters"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Shop by Category nav */}
        <div className="mb-12" data-testid="category-nav">
          <div className="text-center text-[0.65rem] tracking-[0.3em] uppercase text-white/45 mb-4">
            Shop by Category
          </div>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
            {CATEGORIES.map((cat) => {
              const isActive = activeCategory === cat.value;
              return (
                <button
                  key={cat.value}
                  onClick={() => setActiveCategory(cat.value)}
                  className={`px-4 sm:px-5 py-2.5 text-xs sm:text-sm uppercase tracking-[0.16em] border transition-all ${
                    isActive
                      ? "bg-[#D4AF37] border-[#D4AF37] text-black font-semibold"
                      : "border-[#2A2A2A] text-white/70 hover:border-[#D4AF37] hover:text-[#D4AF37]"
                  }`}
                  data-testid={`category-tab-${cat.slug}`}
                >
                  {cat.label}
                </button>
              );
            })}
          </div>

          {activeCategory === "Luxury Wigs" && (
            <div className="mt-5" data-testid="wig-collection-nav">
              <div className="text-center text-[0.6rem] tracking-[0.3em] uppercase text-white/40 mb-3">
                Browse Wig Collection
              </div>
              <div className="flex flex-wrap justify-center gap-2">
                {WIG_COLLECTIONS.map((wc) => {
                  const isActive = activeWigCollection === wc;
                  return (
                    <button
                      key={wc}
                      onClick={() => setActiveWigCollection(wc)}
                      className={`px-3 sm:px-4 py-1.5 text-[0.65rem] sm:text-xs uppercase tracking-[0.18em] border transition-all ${
                        isActive
                          ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/8"
                          : "border-[#2A2A2A] text-white/60 hover:border-[#D4AF37]/60 hover:text-[#D4AF37]"
                      }`}
                      data-testid={`wig-collection-tab-${wc.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {wc}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6" data-testid="collections-grid">
          {filtered.map((c, i) => {
            const gallery = (c.gallery_images || []).filter(Boolean);
            const hasGallery = gallery.length > 0;
            return (
              <motion.article
                key={c.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                className="group flex flex-col bg-[#0A0A0A] border border-[#2A2A2A] hover:border-[#D4AF37]/60 transition-all"
                data-testid={`collection-card-${c.id}`}
              >
                <button
                  type="button"
                  onClick={() => setOpenProduct(c)}
                  className="relative aspect-[4/5] placeholder-frame overflow-hidden text-left"
                  data-testid={`collection-image-${c.id}`}
                  aria-label={`View details for ${c.name}`}
                >
                  <span className="placeholder-tag">{c.name}</span>
                  {c.image ? (
                    <img src={c.image} alt={c.name} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
                  ) : (
                    <div className="placeholder-center">
                      <div className="font-serif text-2xl text-[#D4AF37]">{c.name}</div>
                      <div className="mt-4 w-10 h-px bg-[#D4AF37]/50" />
                      <div className="text-[0.65rem] uppercase tracking-[0.28em] text-white/45 mt-3">
                        Real Product Photo
                      </div>
                    </div>
                  )}
                  {hasGallery && (
                    <div className="absolute bottom-3 right-3 bg-black/70 border border-[#D4AF37]/40 text-[#D4AF37] text-[0.65rem] uppercase tracking-[0.18em] px-2 py-1">
                      +{gallery.length} photos
                    </div>
                  )}
                  {isSaleActive(c) && (
                    <div className="absolute bottom-3 left-3 bg-[#25D366] text-white text-[0.65rem] uppercase tracking-[0.22em] px-2 py-1 font-bold" data-testid={`collection-sale-badge-${c.id}`}>
                      Sale · {discountPercent(c)}% Off
                    </div>
                  )}
                  {c.stock_status === "Out of Stock" && (
                    <div className="absolute inset-0 bg-black/55 flex items-center justify-center" data-testid={`collection-out-of-stock-overlay-${c.id}`}>
                      <div className="border border-[#D4AF37] bg-black/80 px-5 py-2 text-[#D4AF37] text-[0.65rem] tracking-[0.32em] uppercase font-semibold">
                        Out of Stock
                      </div>
                    </div>
                  )}
                  {c.stock_status === "Low Stock" && (
                    <div className="absolute top-3 right-3 bg-amber-500/90 text-black text-[0.6rem] uppercase tracking-[0.22em] px-2 py-1 font-bold" data-testid={`collection-low-stock-badge-${c.id}`}>
                      Low Stock
                    </div>
                  )}
                </button>

                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-serif text-xl text-white" data-testid={`collection-name-${c.id}`}>{c.name}</h3>
                  <p
                    className="text-sm text-white/60 mt-2 leading-relaxed line-clamp-2"
                    data-testid={`collection-desc-${c.id}`}
                  >
                    {c.description}
                  </p>
                  <button
                    type="button"
                    onClick={() => setOpenProduct(c)}
                    className="mt-2 self-start text-[#D4AF37]/80 hover:text-[#D4AF37] text-[0.65rem] font-medium uppercase tracking-[0.18em] flex items-center gap-1 transition-colors"
                    data-testid={`collection-view-details-${c.id}`}
                  >
                    <Eye className="w-3.5 h-3.5" />
                    View Details
                  </button>
                  <div className="mt-auto pt-4 flex items-center justify-between border-t border-[#1F1F1F]">
                    <div data-testid={`collection-price-${c.id}`}>
                      {isSaleActive(c) ? (
                        <>
                          <div className="flex items-center gap-2">
                            <div className="text-[#D4AF37] font-semibold text-sm" data-testid={`collection-price-display-${c.id}`}>
                              {formatPrice(c.sale_price_gbp)}
                            </div>
                            <div className="text-white/70 text-sm font-bold line-through" data-testid={`collection-price-original-${c.id}`}>
                              {formatPrice(c.price_gbp)}
                            </div>
                          </div>
                          <div className="text-[#25D366] text-[0.65rem] tracking-[0.16em] uppercase font-semibold mt-0.5">
                            {discountPercent(c)}% OFF · Shown in {currency}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="text-[#D4AF37] font-semibold text-sm" data-testid={`collection-price-display-${c.id}`}>
                            {formatPrice(c.price_gbp)}
                          </div>
                          <div className="text-white/40 text-[0.65rem] tracking-[0.18em] uppercase mt-0.5">
                            Shown in {currency}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleAdd(c)}
                    disabled={c.stock_status === "Out of Stock"}
                    className={`mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 text-xs uppercase tracking-[0.16em] font-semibold transition-all ${
                      c.stock_status === "Out of Stock"
                        ? "bg-[#1A1A1A] text-white/40 border border-[#2A2A2A] cursor-not-allowed"
                        : justAdded === c.id
                          ? "bg-[#25D366] text-white border border-[#25D366]"
                          : "bg-[#D4AF37] hover:bg-[#F3E5AB] text-black border border-[#D4AF37]"
                    }`}
                    data-testid={`collection-add-to-cart-${c.id}`}
                    aria-label={c.stock_status === "Out of Stock" ? `${c.name} is out of stock` : `Add ${c.name} to bag`}
                  >
                    {c.stock_status === "Out of Stock" ? (
                      <>Out of Stock</>
                    ) : justAdded === c.id ? (
                      <>
                        <Check className="w-4 h-4" />
                        Added to bag
                      </>
                    ) : (
                      <>
                        <ShoppingBag className="w-4 h-4" />
                        Add to bag
                      </>
                    )}
                  </button>
                </div>
              </motion.article>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16 border border-[#2A2A2A] bg-[#0A0A0A]" data-testid="collections-empty">
            <div className="font-serif text-2xl text-white mb-3">Coming soon to this category</div>
            <p className="text-white/55 text-sm max-w-md mx-auto">
              We&rsquo;re curating premium {String(activeCategory || "").toLowerCase()} for you. Chat with us on WhatsApp for personalised options available now.
            </p>
            <a
              href={buildWhatsAppLink(settings, `I'd like to know about your ${activeCategory}.`)}
              target="_blank"
              rel="noreferrer"
              className="btn-whatsapp mt-6 inline-flex"
              data-testid="collections-empty-whatsapp"
            >
              <MessageCircle className="w-4 h-4" />
              Ask on WhatsApp
            </a>
          </div>
        )}
          </>
        )}

        <div className="text-center mt-14">
          <a
            href={buildWhatsAppLink(settings)}
            target="_blank"
            rel="noreferrer"
            className="btn-whatsapp"
            data-testid="collections-whatsapp-button"
          >
            <MessageCircle className="w-5 h-5" />
            {settings?.collections_cta_text}
          </a>
        </div>
      </div>

      {openProduct && (
        <ProductDetailsModal product={openProduct} onClose={() => setOpenProduct(null)} />
      )}
    </section>
  );
}
